import React, {useEffect, useMemo, useState} from "react";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Chips} from "primereact/chips";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";
import {useContentTypeData} from "../../system/use-content-type-data";
import {useTranslate} from "../../system/useTranslate";
import {entityId, entityLabel} from "../operations/operations-utils";
import {FormDialog, FormField, RTL_DROPDOWN_PROPS, SwitchField} from "../sales/components/common";
import {entityKey, getErrorMessage, relationMutation, toDateTimeInput, toISOStringOrNull} from "../sales/sales-utils";

const CAPABILITIES = ["text", "stream", "structured_output", "tool_calling", "embedding", "vision", "audio"];

const DEFINITIONS = {
    [CONTENT_TYPE_UIDS.AI_PROVIDER]: {
        descriptionKey: "ai-admin-provider-form-description",
        defaults: {
            providerKey: "",
            displayName: "",
            providerStatus: "active",
            adapter: "openai",
            credentialEnvKey: "",
            secretConfigured: false,
            capabilities: [],
        },
        fields: [
            {name: "providerKey", labelKey: "ai-admin-provider-key", type: "text", required: true, immutable: true},
            {name: "displayName", labelKey: "ai-admin-engine-display-name", type: "text", required: true},
            {name: "providerStatus", labelKey: "status", type: "enum", values: ["active", "disabled", "degraded"], required: true},
            {name: "adapter", labelKey: "ai-admin-provider-adapter", type: "enum", values: ["openai"], required: true},
            {name: "credentialEnvKey", labelKey: "ai-admin-credential-env-key", type: "text", omitBlankOnEdit: true, ltr: true},
            {name: "secretConfigured", labelKey: "ai-admin-secret-configured", type: "boolean", readOnly: true},
            {name: "capabilities", labelKey: "ai-admin-capabilities", type: "capabilities"},
        ],
    },
    [CONTENT_TYPE_UIDS.AI_MODEL]: {
        descriptionKey: "ai-admin-model-form-description",
        defaults: {
            provider: null,
            modelKey: "",
            displayName: "",
            modelStatus: "active",
            capabilities: ["text"],
            contextWindow: null,
            maxOutputTokens: null,
            supportsStreaming: false,
            supportsStructuredOutput: false,
            supportsToolCalling: false,
            qualityClass: "balanced",
            latencyClass: "standard",
            allowedPlans: [],
            effectiveFrom: "",
            effectiveUntil: "",
        },
        fields: [
            {name: "provider", labelKey: "ai-admin-provider", type: "relation", source: "providers", required: true},
            {name: "modelKey", labelKey: "ai-admin-model-key", type: "text", required: true, immutable: true, ltr: true},
            {name: "displayName", labelKey: "ai-admin-engine-display-name", type: "text", required: true},
            {name: "modelStatus", labelKey: "status", type: "enum", values: ["active", "disabled", "deprecated"], required: true},
            {name: "capabilities", labelKey: "ai-admin-capabilities", type: "capabilities", required: true},
            {name: "contextWindow", labelKey: "ai-admin-context-window", type: "bigint", min: 1},
            {name: "maxOutputTokens", labelKey: "ai-admin-max-output-tokens", type: "number", min: 1},
            {name: "supportsStreaming", labelKey: "ai-admin-supports-streaming", type: "boolean"},
            {name: "supportsStructuredOutput", labelKey: "ai-admin-supports-structured-output", type: "boolean"},
            {name: "supportsToolCalling", labelKey: "ai-admin-supports-tool-calling", type: "boolean"},
            {name: "qualityClass", labelKey: "ai-admin-quality-class", type: "enum", values: ["economy", "balanced", "premium"]},
            {name: "latencyClass", labelKey: "ai-admin-latency-class", type: "enum", values: ["fast", "standard", "slow"]},
            {name: "allowedPlans", labelKey: "ai-admin-allowed-plans", type: "chips"},
            {name: "effectiveFrom", labelKey: "ai-admin-effective-from", type: "datetime"},
            {name: "effectiveUntil", labelKey: "ai-admin-effective-until", type: "datetime"},
        ],
    },
    [CONTENT_TYPE_UIDS.AI_MODEL_PRICE]: {
        descriptionKey: "ai-admin-price-form-description",
        defaults: {
            model: null,
            currency: "USD",
            inputMicrosPerMillion: "0",
            outputMicrosPerMillion: "0",
            cachedInputMicrosPerMillion: "0",
            priceStatus: "active",
            effectiveFrom: "",
            effectiveUntil: "",
        },
        fields: [
            {name: "model", labelKey: "ai-admin-model", type: "relation", source: "models", required: true},
            {name: "currency", labelKey: "billing-admin-currency", type: "enum", values: ["USD"], required: true},
            {name: "inputMicrosPerMillion", labelKey: "ai-admin-input-price", type: "bigint", min: 0, required: true},
            {name: "outputMicrosPerMillion", labelKey: "ai-admin-output-price", type: "bigint", min: 0, required: true},
            {name: "cachedInputMicrosPerMillion", labelKey: "ai-admin-cached-input-price", type: "bigint", min: 0},
            {name: "priceStatus", labelKey: "status", type: "enum", values: ["scheduled", "active", "expired"], required: true},
            {name: "effectiveFrom", labelKey: "ai-admin-effective-from", type: "datetime", required: true},
            {name: "effectiveUntil", labelKey: "ai-admin-effective-until", type: "datetime"},
        ],
    },
    [CONTENT_TYPE_UIDS.AI_PROMPT]: {
        descriptionKey: "ai-admin-prompt-form-description",
        defaults: {
            engine: null,
            promptKey: "",
            version: 1,
            locale: "fa",
            promptStatus: "draft",
            systemInstructions: "",
            inputSchema: {type: "object", properties: {}, required: [], additionalProperties: false},
            outputSchema: {type: "object", properties: {}, required: [], additionalProperties: false},
            supportedModels: [],
            publishedAt: "",
            changeNotes: "",
        },
        fields: [
            {name: "engine", labelKey: "ai-admin-engine", type: "relation", source: "engines", required: true},
            {name: "promptKey", labelKey: "ai-admin-prompt-key", type: "text", required: true, immutable: true, ltr: true},
            {name: "version", labelKey: "ai-admin-version", type: "number", min: 1, required: true},
            {name: "locale", labelKey: "ai-admin-locale", type: "text", required: true, ltr: true},
            {name: "promptStatus", labelKey: "status", type: "enum", values: ["draft", "published", "retired"], required: true},
            {name: "systemInstructions", labelKey: "ai-admin-system-instructions", type: "textarea", requiredOnCreate: true, omitBlankOnEdit: true, full: true},
            {name: "inputSchema", labelKey: "ai-admin-input-schema", type: "schema", full: true},
            {name: "outputSchema", labelKey: "ai-admin-output-schema", type: "schema", full: true},
            {name: "supportedModels", labelKey: "ai-admin-supported-models", type: "modelKeys", full: true},
            {name: "publishedAt", labelKey: "ai-admin-published-at", type: "datetime"},
            {name: "changeNotes", labelKey: "ai-admin-change-notes", type: "textarea", full: true},
        ],
    },
    [CONTENT_TYPE_UIDS.WORKSPACE_AI_SETTING]: {
        descriptionKey: "ai-admin-workspace-setting-form-description",
        defaults: {
            workspace: null,
            aiEnabled: true,
            scopeMode: "strict",
            creditMode: "legacy_unmetered",
            allowFallback: true,
            allowAutoDowngrade: false,
            retentionDays: 90,
            monthlyCreditLimit: null,
            dailyCreditLimit: null,
            creditsPerUsd: 1000,
            settingsVersion: 1,
        },
        fields: [
            {name: "workspace", labelKey: "workspace", type: "relation", source: "workspaces", required: true},
            {name: "aiEnabled", labelKey: "ai-admin-ai-enabled", type: "boolean"},
            {name: "scopeMode", labelKey: "ai-admin-scope-mode", type: "enum", values: ["strict", "workspace_only"], required: true},
            {name: "creditMode", labelKey: "ai-admin-credit-mode", type: "enum", values: ["legacy_unmetered", "metered", "paused"], required: true},
            {name: "allowFallback", labelKey: "ai-admin-allow-fallback", type: "boolean"},
            {name: "allowAutoDowngrade", labelKey: "ai-admin-auto-downgrade", type: "boolean"},
            {name: "retentionDays", labelKey: "ai-admin-retention-days", type: "number", min: 1},
            {name: "monthlyCreditLimit", labelKey: "ai-admin-monthly-credit-cap", type: "bigint", min: 0},
            {name: "dailyCreditLimit", labelKey: "ai-admin-daily-credit-cap", type: "bigint", min: 0},
            {name: "creditsPerUsd", labelKey: "ai-admin-credits-per-usd", type: "number", min: 1, required: true},
            {name: "settingsVersion", labelKey: "ai-admin-settings-version", type: "number", min: 1, required: true},
        ],
    },
    [CONTENT_TYPE_UIDS.AI_BUDGET_POLICY]: {
        descriptionKey: "ai-admin-budget-policy-form-description",
        defaults: {
            workspace: null,
            engineKey: "",
            modelKey: "",
            membership: null,
            policyType: "hard_limit",
            dailyLimit: null,
            monthlyLimit: null,
            perRunLimit: null,
            budgetPolicyStatus: "active",
            effectiveFrom: "",
            effectiveUntil: "",
        },
        fields: [
            {name: "workspace", labelKey: "workspace", type: "relation", source: "workspaces"},
            {name: "engineKey", labelKey: "ai-admin-engine-key", type: "engineKey"},
            {name: "modelKey", labelKey: "ai-admin-model-key", type: "modelKey"},
            {name: "membership", labelKey: "ai-admin-membership", type: "relation", source: "memberships"},
            {name: "policyType", labelKey: "ai-admin-policy-type", type: "enum", values: ["hard_limit", "soft_limit", "approval_required", "auto_downgrade"], required: true},
            {name: "dailyLimit", labelKey: "ai-admin-daily-credit-cap", type: "bigint", min: 0},
            {name: "monthlyLimit", labelKey: "ai-admin-monthly-credit-cap", type: "bigint", min: 0},
            {name: "perRunLimit", labelKey: "ai-admin-per-run-limit", type: "bigint", min: 0},
            {name: "budgetPolicyStatus", labelKey: "status", type: "enum", values: ["active", "disabled"], required: true},
            {name: "effectiveFrom", labelKey: "ai-admin-effective-from", type: "datetime"},
            {name: "effectiveUntil", labelKey: "ai-admin-effective-until", type: "datetime"},
        ],
    },
    [CONTENT_TYPE_UIDS.AI_RATE_LIMIT_POLICY]: {
        descriptionKey: "ai-admin-rate-policy-form-description",
        defaults: {
            workspace: null,
            engineKey: "",
            membership: null,
            requestsPerMinute: 10,
            requestsPerDay: null,
            concurrentRuns: 2,
            rateLimitPolicyStatus: "active",
        },
        fields: [
            {name: "workspace", labelKey: "workspace", type: "relation", source: "workspaces"},
            {name: "engineKey", labelKey: "ai-admin-engine-key", type: "engineKey"},
            {name: "membership", labelKey: "ai-admin-membership", type: "relation", source: "memberships"},
            {name: "requestsPerMinute", labelKey: "ai-admin-requests-per-minute", type: "number", min: 1, required: true},
            {name: "requestsPerDay", labelKey: "ai-admin-requests-per-day", type: "number", min: 1},
            {name: "concurrentRuns", labelKey: "ai-admin-concurrent-runs", type: "number", min: 1, required: true},
            {name: "rateLimitPolicyStatus", labelKey: "status", type: "enum", values: ["active", "disabled"], required: true},
        ],
    },
};

function normalizedArray(value) {
    if (Array.isArray(value)) return value.map((item) => String(item?.key || item?.value || item)).filter(Boolean);
    if (value && typeof value === "object") {
        return Object.entries(value).filter(([, enabled]) => Boolean(enabled)).map(([key]) => key);
    }
    return [];
}

function normalizeSchema(value) {
    if (!value || Array.isArray(value) || typeof value !== "object") {
        return {type: "object", properties: {}, required: [], additionalProperties: false};
    }
    return {
        ...value,
        type: value.type || "object",
        properties: value.properties && typeof value.properties === "object" ? value.properties : {},
        required: Array.isArray(value.required) ? value.required : [],
        additionalProperties: value.additionalProperties === true,
    };
}

function schemaRows(schema) {
    return Object.entries(schema.properties || {}).map(([name, property]) => ({
        name,
        type: property?.type || "string",
        description: property?.description || "",
        required: (schema.required || []).includes(name),
    }));
}

function JsonSchemaForm({label, value, onChange}) {
    const translate = useTranslate();
    const schema = normalizeSchema(value);
    const rows = schemaRows(schema);
    const setRows = (nextRows) => {
        const properties = {};
        const required = [];
        nextRows.forEach((row) => {
            const name = String(row.name || "").trim();
            if (!name) return;
            properties[name] = {
                type: row.type || "string",
                ...(String(row.description || "").trim() ? {description: String(row.description).trim()} : {}),
            };
            if (row.required) required.push(name);
        });
        onChange({...schema, properties, required});
    };
    const updateRow = (index, patch) => setRows(rows.map((row, rowIndex) => rowIndex === index ? {...row, ...patch} : row));

    return (
        <div className="col-12 flex flex-column gap-3 border-1 border-200 border-round-xl p-3">
            <div>
                <div className="font-bold text-sm">{label}</div>
                <small className="text-xs text-500 line-height-3">{translate("ai-admin-schema-builder-hint")}</small>
            </div>
            <div className="grid">
                <FormField label={translate("ai-admin-schema-type")} hint={translate("ai-admin-schema-type-hint")}>
                    <Dropdown
                        {...RTL_DROPDOWN_PROPS}
                        value={schema.type}
                        options={["object", "array", "string", "number", "boolean"].map((item) => ({
                            value: item,
                            label: translate(`ai-admin-schema-${item}`),
                        }))}
                        onChange={(event) => onChange({...schema, type: event.value})}
                    />
                </FormField>
                <SwitchField
                    label={translate("ai-admin-additional-properties")}
                    hint={translate("ai-admin-additional-properties-hint")}
                    checked={schema.additionalProperties}
                    onChange={(checked) => onChange({...schema, additionalProperties: checked})}
                />
            </div>
            {schema.type === "object" && rows.map((row, index) => (
                <div className="grid surface-50 border-1 border-200 border-round p-2" key={`${row.name}-${index}`}>
                    <FormField label={translate("ai-admin-property-name")} hint={translate("ai-admin-property-name-hint")}>
                        <InputText className="w-full ltr text-xs" value={row.name} onChange={(event) => updateRow(index, {name: event.target.value})}/>
                    </FormField>
                    <FormField label={translate("ai-admin-property-type")} hint={translate("ai-admin-property-type-hint")}>
                        <Dropdown
                            {...RTL_DROPDOWN_PROPS}
                            value={row.type}
                            options={["string", "number", "integer", "boolean", "array", "object"].map((item) => ({
                                value: item,
                                label: translate(`ai-admin-schema-${item}`),
                            }))}
                            onChange={(event) => updateRow(index, {type: event.value})}
                        />
                    </FormField>
                    <FormField className="col-12 md:col-8" label={translate("description")} hint={translate("ai-admin-property-description-hint")}>
                        <InputText className="w-full text-xs" value={row.description} onChange={(event) => updateRow(index, {description: event.target.value})}/>
                    </FormField>
                    <div className="col-12 md:col-4 flex align-items-center justify-content-between gap-2">
                        <label className="flex align-items-center gap-2 text-xs">
                            <Checkbox checked={row.required} onChange={(event) => updateRow(index, {required: event.checked})}/>
                            <span>{translate("required")}</span>
                        </label>
                        <Button
                            type="button"
                            icon="pi pi-trash"
                            className="gap-2"
                            severity="danger"
                            text
                            aria-label={translate("remove")}
                            onClick={() => setRows(rows.filter((_, rowIndex) => rowIndex !== index))}
                        />
                    </div>
                </div>
            ))}
            {schema.type === "object" && (
                <Button
                    type="button"
                    icon="pi pi-plus"
                    className="gap-2 text-xs align-self-start"
                    outlined
                    label={translate("ai-admin-add-schema-property")}
                    onClick={() => setRows([...rows, {name: "", type: "string", description: "", required: false}])}
                />
            )}
        </div>
    );
}

function fieldHintKey(field) {
    return field.hintKey || `ai-admin-field-${field.name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}-hint`;
}

function relationOptions(items) {
    return items.map((item) => ({value: entityId(item), label: entityLabel(item)})).filter((item) => item.value);
}

function prepareForm(definition, entity) {
    const next = {...definition.defaults};
    definition.fields.forEach((field) => {
        const value = entity?.[field.name];
        if (value == null) return;
        if (field.type === "relation") next[field.name] = entityId(value);
        else if (field.type === "datetime") next[field.name] = toDateTimeInput(value);
        else if (["capabilities", "chips", "modelKeys"].includes(field.type)) next[field.name] = normalizedArray(value);
        else if (field.type === "schema") next[field.name] = normalizeSchema(value);
        else next[field.name] = value;
    });
    if (entity) {
        definition.fields.filter((field) => field.omitBlankOnEdit).forEach((field) => {
            next[field.name] = "";
        });
    }
    return next;
}

function requiredValueMissing(field, value, entity) {
    if (!(field.required || (!entity && field.requiredOnCreate))) return false;
    if (Array.isArray(value)) return value.length === 0;
    return value == null || String(value).trim() === "";
}

export default function AiStructuredEntityEditor({uid, visible, entity, data, onHide}) {
    const translate = useTranslate();
    const providers = useContentTypeData(CONTENT_TYPE_UIDS.AI_PROVIDER);
    const models = useContentTypeData(CONTENT_TYPE_UIDS.AI_MODEL);
    const engines = useContentTypeData(CONTENT_TYPE_UIDS.AI_ENGINE);
    const workspaces = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE);
    const memberships = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE_MEMBERSHIP);
    const definition = DEFINITIONS[uid];
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!visible || !definition) return;
        setForm(prepareForm(definition, entity));
        setError(null);
    }, [definition, entity, visible]);

    useEffect(() => {
        if (!visible || !definition) return;
        const sources = new Set(definition.fields.map((field) => field.source).filter(Boolean));
        const requests = [];
        if (sources.has("providers")) requests.push(providers.ensure({}, {all: true}));
        if (sources.has("models")) requests.push(models.ensure({}, {all: true}));
        if (sources.has("engines")) requests.push(engines.ensure({}, {all: true}));
        if (sources.has("workspaces")) requests.push(workspaces.ensure({}, {all: true}));
        if (sources.has("memberships")) requests.push(memberships.ensure({}, {all: true}));
        if (definition.fields.some((field) => ["modelKey", "modelKeys"].includes(field.type))) requests.push(models.ensure({}, {all: true}));
        if (definition.fields.some((field) => field.type === "engineKey")) requests.push(engines.ensure({}, {all: true}));
        Promise.allSettled(requests);
        // Content-type methods are stable for their UID; requests depend on the selected definition.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [definition, visible]);

    const sources = useMemo(() => ({
        providers: relationOptions(providers.items),
        models: relationOptions(models.items),
        engines: relationOptions(engines.items),
        workspaces: relationOptions(workspaces.items),
        memberships: relationOptions(memberships.items),
    }), [engines.items, memberships.items, models.items, providers.items, workspaces.items]);
    const modelKeyOptions = useMemo(() => models.items.map((item) => ({
        value: item.modelKey,
        label: item.displayName || item.modelKey,
    })).filter((item) => item.value), [models.items]);
    const engineKeyOptions = useMemo(() => engines.items.map((item) => ({
        value: item.engineKey,
        label: item.displayName || item.engineKey,
    })).filter((item) => item.value), [engines.items]);

    if (!definition) return null;

    const setField = (name, value) => setForm((current) => ({...current, [name]: value}));
    const invalid = definition.fields.some((field) => requiredValueMissing(field, form[field.name], entity));
    const buildPayload = () => {
        const payload = {};
        definition.fields.forEach((field) => {
            const value = form[field.name];
            if (entity && field.omitBlankOnEdit && !String(value || "").trim()) return;
            if (field.readOnly && entity) return;
            if (field.type === "relation") payload[field.name] = relationMutation(value, entity?.[field.name]);
            else if (field.type === "datetime") payload[field.name] = toISOStringOrNull(value);
            else if (field.type === "bigint") payload[field.name] = value == null || value === "" ? null : String(value);
            else if (["capabilities", "chips", "modelKeys"].includes(field.type)) payload[field.name] = normalizedArray(value);
            else if (field.type === "schema") payload[field.name] = normalizeSchema(value);
            else payload[field.name] = value;
        });
        return payload;
    };
    const save = async (event) => {
        event.preventDefault();
        if (invalid) return;
        setSaving(true);
        setError(null);
        try {
            const payload = buildPayload();
            if (entity) await data.update(entityKey(entity), payload);
            else await data.create(payload);
            onHide();
        } catch (requestError) {
            setError(getErrorMessage(requestError, translate("ai-admin-save-error")));
        } finally {
            setSaving(false);
        }
    };
    const optionLabel = (value) => translate(`ai-admin-option-${String(value).replace(/_/g, "-").toLowerCase()}`);

    const renderField = (field) => {
        const label = translate(field.labelKey);
        const hint = translate(fieldHintKey(field));
        const common = {label, hint, required: field.required || (!entity && field.requiredOnCreate)};
        if (field.type === "boolean") {
            return (
                <SwitchField
                    key={field.name}
                    label={label}
                    hint={hint}
                    checked={Boolean(form[field.name])}
                    disabled={field.readOnly}
                    onChange={(value) => setField(field.name, value)}
                />
            );
        }
        if (field.type === "schema") {
            return <JsonSchemaForm key={field.name} label={label} value={form[field.name]} onChange={(value) => setField(field.name, value)}/>;
        }
        let control;
        if (field.type === "relation") {
            control = <Dropdown {...RTL_DROPDOWN_PROPS} filter showClear={!field.required} value={form[field.name]} options={sources[field.source] || []} onChange={(event) => setField(field.name, event.value)}/>;
        } else if (field.type === "enum") {
            control = <Dropdown {...RTL_DROPDOWN_PROPS} value={form[field.name]} options={field.values.map((value) => ({value, label: optionLabel(value)}))} onChange={(event) => setField(field.name, event.value)}/>;
        } else if (field.type === "capabilities") {
            control = (
                <div className="grid m-0">
                    {CAPABILITIES.map((capability) => (
                        <label className="col-12 md:col-6 flex align-items-center gap-2 text-xs" key={capability}>
                            <Checkbox
                                checked={normalizedArray(form[field.name]).includes(capability)}
                                onChange={(event) => setField(
                                    field.name,
                                    event.checked
                                        ? [...normalizedArray(form[field.name]), capability]
                                        : normalizedArray(form[field.name]).filter((item) => item !== capability)
                                )}
                            />
                            <span>{translate(`ai-admin-capability-${capability.replace(/_/g, "-")}`)}</span>
                        </label>
                    ))}
                </div>
            );
        } else if (field.type === "chips") {
            control = <Chips className="w-full" inputClassName="w-full ltr text-xs" value={form[field.name] || []} separator="," onChange={(event) => setField(field.name, event.value)}/>;
        } else if (field.type === "modelKeys") {
            control = (
                <div className="grid m-0">
                    {modelKeyOptions.map((option) => (
                        <label className="col-12 md:col-6 flex align-items-center gap-2 text-xs" key={option.value}>
                            <Checkbox
                                checked={normalizedArray(form[field.name]).includes(option.value)}
                                onChange={(event) => setField(
                                    field.name,
                                    event.checked
                                        ? [...normalizedArray(form[field.name]), option.value]
                                        : normalizedArray(form[field.name]).filter((item) => item !== option.value)
                                )}
                            />
                            <span>{option.label}</span>
                        </label>
                    ))}
                </div>
            );
        } else if (field.type === "engineKey" || field.type === "modelKey") {
            control = <Dropdown {...RTL_DROPDOWN_PROPS} filter showClear value={form[field.name]} options={field.type === "engineKey" ? engineKeyOptions : modelKeyOptions} onChange={(event) => setField(field.name, event.value)}/>;
        } else if (field.type === "number") {
            control = <InputNumber className="w-full" inputClassName="w-full ltr" value={form[field.name]} min={field.min} max={field.max} useGrouping={false} onValueChange={(event) => setField(field.name, event.value)}/>;
        } else if (field.type === "bigint") {
            control = <InputText className="w-full ltr text-xs" inputMode="numeric" value={form[field.name] ?? ""} onChange={(event) => setField(field.name, event.target.value.replace(/[^\d]/g, ""))}/>;
        } else if (field.type === "datetime") {
            control = <InputText className="w-full ltr text-xs" type="datetime-local" value={form[field.name] || ""} onChange={(event) => setField(field.name, event.target.value)}/>;
        } else if (field.type === "textarea") {
            control = <InputTextarea className="w-full text-xs" rows={5} value={form[field.name] || ""} onChange={(event) => setField(field.name, event.target.value)}/>;
        } else {
            control = <InputText className={`w-full text-xs ${field.ltr ? "ltr" : ""}`} disabled={field.immutable && Boolean(entity)} value={form[field.name] ?? ""} onChange={(event) => setField(field.name, event.target.value)}/>;
        }
        return (
            <FormField key={field.name} className={field.full ? "col-12" : undefined} {...common}>
                {control}
            </FormField>
        );
    };

    return (
        <FormDialog
            visible={visible}
            title={translate(entity ? "ai-admin-edit-title" : "ai-admin-create-title")}
            description={translate(definition.descriptionKey)}
            saving={saving}
            error={error}
            onHide={() => !saving && onHide()}
            onSubmit={save}
            submitDisabled={invalid}
            className="rtl w-11 md:w-10 lg:w-8 xl:w-7 border-round-bottom-2xl"
        >
            <div className="col-12">
                <Message severity="info" className="w-full text-xs" text={translate("ai-admin-structured-form-notice")}/>
            </div>
            {definition.fields.map(renderField)}
        </FormDialog>
    );
}
